import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { addToast } from 'src/old/actions';
import { useConnectCastillaMutation, useSaveCastillaMutation } from 'src/shared/api/integrations/castilla';
import { useGetIntegrationDataQuery } from 'src/shared/api/integrations/factorial';
import { toastTypes } from 'src/shared/components/Toast/ToastOptions';
import { getIsConnected } from '../utils/getIsConnected';
import { useForm, useFormState } from 'react-final-form';
import { type CastillaValues, mapFormDataToCastillaConfig, step2InputList } from './utils';
import { useGetCompaniesQuery } from 'src/shared/api/company';
import { isEmpty } from 'lodash';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';

const integrationName = 'castilla';

export const useCastillaConnection = () => {
  const form = useForm();
  const { data: companies } = useGetCompaniesQuery();
  const [selectedCompany, setSelectedCompany] = useState('');
  const { data: integrationData, isLoading } = useGetIntegrationDataQuery();
  const [triggerConnection] = useConnectCastillaMutation();
  const { values } = useFormState();
  const isConnected = getIsConnected({
    isLoading,
    integrationData,
    subsidiary: values.subsidiary,
    integrationName
  });
  const company = integrationData?.find(company => company.subsidiary === selectedCompany);

  const handleSelectedCompanyChange = (newCompany: string) => {
    setSelectedCompany(newCompany);
  };

  useEffect(() => {
    if (!integrationData || !companies || !selectedCompany) {
      return;
    }

    if (company) {
      form.change('baseURL', company?.credentials?.baseURL);
      form.change('appName', company?.credentials?.appName);
      form.change('appToken', company?.credentials?.appToken);
      form.change('recordTransactions', company?.flowsEnabled?.batchPaymentSync);
      form.change('idEmpresa', company?.parameters?.idEmpresa);
      form.change('idConcepto', company?.parameters?.idConcepto);

      if (!isEmpty(company?.fieldMap?.fieldMapping)) {
        form.change('withCustomFields', true);
        form.change('customFields', company?.fieldMap?.fieldMapping);
      } else {
        form.change('withCustomFields', false);
        form.change('customFields', null);
      }
    } else {
      form.change('baseURL', '');
      form.change('appName', '');
      form.change('appToken', '');
      form.change('recordTransactions', false);
      form.change('idEmpresa', '');
      form.change('idConcepto', '');

      form.change('withCustomFields', false);
      form.change('customFields', null);
    }
  }, [integrationData, form, companies, selectedCompany, company, company?.flowsEnabled?.batchPaymentSync]);

  const isConnectBtnDisabled = useMemo(() => {
    const unchangedCredentials = step2InputList.every(
      input => values[input.name] === company?.credentials?.[input.name]
    );
    const allFilled = step2InputList.every(input => values[input.name]);

    return unchangedCredentials || !allFilled;
  }, [values, company]);

  return {
    selectedCompany,
    handleSelectedCompanyChange,
    triggerConnection,
    isConnected,
    isConnectBtnDisabled
  };
};

export const useCastillaSaveIntegration = () => {
  const [saveConfig] = useSaveCastillaMutation();
  const dispatch = useDispatch();
  const { t } = useTranslation('superAdmin');

  const saveIntegrationData = () => (formData: CastillaValues) => async () => {
    try {
      const payload = mapFormDataToCastillaConfig(formData);
      await saveConfig(payload).unwrap();
      dispatch(addToast({ ...toastTypes.success, defaultMessage: t('toasts.updated') }));
    } catch (error) {
      console.error(error);
      const defaultMessage =
        (error as FetchBaseQueryError)?.status === 400 ? t('toasts.error.invalid data') : t('toasts.error');
      dispatch(addToast({ ...toastTypes.error, defaultMessage }));
    }
  };

  return saveIntegrationData;
};
